import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const productGroups = [
  {
    label: "Down To Film Apparel",
    color: "var(--dtf-blue)",
    options: [
      "T-Shirts (DTF)",
      "Razor Back Tank Tops (DTF)",
      "Hoodies (DTF)",
      "Zip-Up Hoodies (DTF)",
      "DTF Gang Sheets (14\" x 18\")",
    ],
  },
  {
    label: "The Sticky Chick",
    color: "var(--sc-purple)",
    options: [
      "Stickers (Peel & Stick)",
      "Decals (Transfer Tape)",
      "Credit/Debit Card Stickers",
      "Label Sheets",
      "Door Signs",
      "Signage",
      "Misc Marketing Material",
    ],
  },
  {
    label: "Other",
    color: "#111",
    options: ["Other — please describe below"],
  },
];

const defaultProductType = productGroups[0].options[0];

const initial = {
  name: "",
  email: "",
  phone: "",
  product_type: defaultProductType,
  quantity: "",
  deadline: "",
  description: "",
};

export default function Quote() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.quantity || !form.description) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/quote", form);
      setDone(true);
      toast.success("Quote request received! We'll be in touch shortly.");
      setForm(initial);
    } catch (err) {
      console.error(err);
      toast.error("Could not submit quote. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="quote-page">
      <section className="border-b-2 border-black">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-20">
          <p className="label-mono text-[color:var(--muted-text)]">WORK ORDER / REQUEST FORM</p>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.9] mt-3">
            GET A QUOTE.
          </h1>
          <p className="font-body text-[color:var(--muted-text)] max-w-xl mt-4">
            Tell us what you need. We usually reply within one business day with
            pricing, timeline, and options.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-12 md:py-16">
        {done ? (
          <div className="brutal-card p-10 hard-shadow-red max-w-2xl mx-auto text-center" data-testid="quote-success">
            <CheckCircle2 size={48} className="mx-auto text-[color:var(--primary)]" />
            <h2 className="font-display text-4xl mt-4">REQUEST RECEIVED.</h2>
            <p className="font-body text-[color:var(--muted-text)] mt-3">
              Thanks — we&rsquo;ll be in touch within one business day.
            </p>
            <button
              className="btn-brutal btn-brutal-outline mt-6"
              onClick={() => setDone(false)}
              data-testid="new-quote-btn"
            >
              Submit Another Quote
            </button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="grid grid-cols-1 md:grid-cols-12 gap-8 max-w-5xl mx-auto"
            data-testid="quote-form"
          >
            <div className="md:col-span-4">
              <div className="brutal-card p-6">
                <p className="label-mono text-[color:var(--primary)]">FORM · 001</p>
                <h3 className="font-display text-3xl mt-2 leading-none">CUSTOMIZE<br />YOUR ORDER!</h3>
                <p className="font-body text-sm text-[color:var(--muted-text)] mt-4">
                  All fields marked <span className="text-[color:var(--primary)]">*</span> are required.
                  We&rsquo;ll respond with an itemized quote and proof timeline.
                </p>
                <div className="mt-6 border-t border-black pt-4 space-y-2">
                  <p className="label-mono">TURNAROUND · 3–10 DAYS</p>
                  <p className="label-mono">MIN QTY · NONE</p>
                  <p className="label-mono">MADE IN · USA</p>
                </div>
                <div className="mt-6 border-t border-black pt-4">
                  <p className="label-mono text-[color:var(--muted-text)]">Prefer to call?</p>
                  <a
                    href="tel:+19099313557"
                    className="font-mono text-lg font-bold mt-2 inline-block hover:text-[color:var(--primary)]"
                    data-testid="quote-phone-link"
                  >
                    (909) 931-3557
                  </a>
                </div>
              </div>
            </div>

            <div className="md:col-span-8 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="label-mono block mb-2">Name <span className="text-[color:var(--primary)]">*</span></label>
                  <input
                    value={form.name}
                    onChange={update("name")}
                    className="brutal-input"
                    required
                    data-testid="quote-name-input"
                  />
                </div>
                <div>
                  <label className="label-mono block mb-2">Email <span className="text-[color:var(--primary)]">*</span></label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="brutal-input"
                    required
                    data-testid="quote-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="label-mono block mb-2">Phone</label>
                  <input
                    value={form.phone}
                    onChange={update("phone")}
                    className="brutal-input"
                    data-testid="quote-phone-input"
                  />
                </div>
                <div>
                  <label className="label-mono block mb-2">Deadline</label>
                  <input
                    type="date"
                    value={form.deadline}
                    onChange={update("deadline")}
                    className="brutal-input"
                    data-testid="quote-deadline-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="label-mono block mb-2">Product Type <span className="text-[color:var(--primary)]">*</span></label>
                  <select
                    value={form.product_type}
                    onChange={update("product_type")}
                    className="brutal-input"
                    data-testid="quote-product-type-select"
                  >
                    {productGroups.map((g) => (
                      <optgroup
                        key={g.label}
                        label={`— ${g.label} —`}
                        style={{ color: g.color, fontWeight: 700 }}
                      >
                        {g.options.map((p) => (
                          <option key={p} value={p} style={{ color: "#111", fontWeight: 400 }}>
                            {p}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {form.product_type === "Misc Marketing Material" && (
                    <div
                      className="mt-3 border border-black bg-white p-3 flex items-start gap-2"
                      data-testid="quote-misc-notice"
                    >
                      <AlertTriangle size={16} className="text-black mt-0.5 shrink-0" />
                      <div>
                        <p className="label-mono text-[11px]">Production Notice</p>
                        <p className="font-body text-xs text-[color:var(--muted-text)] mt-1">
                          Misc marketing material does <strong className="text-black">not</strong> ship on our 2-day timeline.
                          Please allow <strong className="text-black">8–10 business days</strong> for production before shipping.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="label-mono block mb-2">Quantity <span className="text-[color:var(--primary)]">*</span></label>
                  <input
                    value={form.quantity}
                    onChange={update("quantity")}
                    className="brutal-input"
                    placeholder="e.g. 50 shirts, 200 stickers"
                    required
                    data-testid="quote-quantity-input"
                  />
                </div>
              </div>

              <div>
                <label className="label-mono block mb-2">Project Details <span className="text-[color:var(--primary)]">*</span></label>
                <textarea
                  rows={6}
                  value={form.description}
                  onChange={update("description")}
                  className="brutal-input resize-none"
                  placeholder="Tell us about sizes, colors, print locations, artwork status, any deadlines, etc."
                  required
                  data-testid="quote-description-input"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="btn-brutal btn-brutal-red disabled:opacity-60"
                data-testid="quote-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending...
                  </>
                ) : (
                  <>Submit Work Order</>
                )}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
